import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Bookmark, Plus, X } from "lucide-react";

const isActive = (f) =>
  !!(
    (f.q && f.q.trim()) ||
    (f.status && f.status !== "all") ||
    (f.source && f.source !== "all") ||
    (f.category && f.category !== "all") ||
    (f.location && f.location !== "all")
  );

export default function SavedSegments({ filters, onApply }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const { data: segments = [] } = useQuery({
    queryKey: ["segments"],
    queryFn: () => api.get("/segments").then((r) => r.data),
  });

  const save = async () => {
    if (!name.trim()) {
      toast.error("Name your segment first");
      return;
    }
    try {
      await api.post("/segments", { name: name.trim(), ...filters });
      toast.success(`Saved segment "${name.trim()}"`);
      setName("");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["segments"] });
    } catch (e) {
      toast.error("Could not save segment");
    }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/segments/${id}`);
      qc.invalidateQueries({ queryKey: ["segments"] });
    } catch (e2) {
      toast.error("Could not delete segment");
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="saved-segments-bar">
      <span className="text-xs text-muted-foreground flex items-center gap-1.5 mr-1">
        <Bookmark className="h-3.5 w-3.5" /> Segments
      </span>
      {segments.length === 0 && (
        <span className="text-xs text-muted-foreground/60">none saved yet</span>
      )}
      {segments.map((s) => (
        <button
          key={s.id}
          onClick={() => onApply(s.filters)}
          className="group inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs hover:border-primary/50 hover:bg-primary/5 transition-colors"
          data-testid={`segment-chip-${s.id}`}
        >
          {s.name}
          <X
            className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-rose-400 transition-opacity"
            onClick={(e) => remove(e, s.id)}
            data-testid={`segment-delete-${s.id}`}
          />
        </button>
      ))}
      <Button
        size="sm"
        variant="outline"
        className="h-7 rounded-full text-xs"
        disabled={!isActive(filters)}
        onClick={() => setOpen(true)}
        data-testid="save-segment-button"
      >
        <Plus className="h-3.5 w-3.5 mr-1" /> Save view
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover border-border" data-testid="save-segment-dialog">
          <DialogHeader>
            <DialogTitle>Save this segment</DialogTitle>
            <DialogDescription>Save your current filters as a one-click view you can reuse.</DialogDescription>
          </DialogHeader>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Windsor salons — New"
            onKeyDown={(e) => e.key === "Enter" && save()}
            data-testid="segment-name-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="segment-save-confirm">Save segment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
